import { useEffect, useRef, useState } from 'react'
import { CheckCircle, Copy, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { usePaymentQR, useSalesOrder } from '@/hooks/use-sales-order-query'
import { formatVND } from '@/lib/format'
import { orderRemaining } from '@/lib/order-money'
import { m } from '@/lib/paraglide/messages'

export interface PaymentQRDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    orderId: string
    onPaymentConfirmed?: () => void
}

export function PaymentQRDialog({
    open,
    onOpenChange,
    orderId,
    onPaymentConfirmed,
}: PaymentQRDialogProps) {
    const [confirmed, setConfirmed] = useState(false)
    const confirmedRef = useRef(false)

    const { data: qr, isLoading, isError } = usePaymentQR(orderId, open)
    const { data: order } = useSalesOrder(orderId)

    // Detect payment confirmation by polling order detail
    useEffect(() => {
        if (!open || confirmedRef.current) return
        if (!order) return

        const remaining = orderRemaining(order)
        if (remaining <= 0 && order.payment_status === 'paid') {
            confirmedRef.current = true
            setConfirmed(true)
            toast.success(m.payment_qr_confirmed())
            onPaymentConfirmed?.()
            const timer = setTimeout(() => {
                onOpenChange(false)
            }, 2000)
            return () => clearTimeout(timer)
        }
    }, [order, open, onOpenChange, onPaymentConfirmed])

    // Reset state when dialog closes
    useEffect(() => {
        if (!open) {
            setConfirmed(false)
            confirmedRef.current = false
        }
    }, [open])

    async function copyText(text: string) {
        try {
            await navigator.clipboard.writeText(text)
            toast.success(m.payment_qr_copied())
        } catch {
            // ignore
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-sm">
                <DialogHeader>
                    <DialogTitle>{m.payment_qr_title()}</DialogTitle>
                </DialogHeader>

                {confirmed ? (
                    <div className="flex flex-col items-center gap-3 py-6">
                        <CheckCircle className="size-16 text-green-500" />
                        <p className="text-base font-semibold text-green-600">
                            {m.payment_qr_confirmed()}
                        </p>
                    </div>
                ) : isLoading ? (
                    <div className="flex flex-col items-center gap-3 py-6">
                        <Loader2 className="size-8 animate-spin text-muted-foreground" />
                        <p className="text-sm text-muted-foreground">
                            {m.payment_qr_loading()}
                        </p>
                    </div>
                ) : isError || !qr ? (
                    <div className="py-6 text-center text-sm text-destructive">
                        {m.payment_qr_error()}
                    </div>
                ) : (
                    <div className="space-y-4">
                        {/* QR code */}
                        <div className="flex justify-center">
                            <img
                                src={qr.qr_url}
                                alt="QR Code"
                                className="size-48 rounded border object-contain"
                            />
                        </div>

                        {/* Bank info */}
                        <div className="space-y-2 rounded-md bg-muted/40 p-3 text-sm">
                            <InfoRow
                                label={m.payment_qr_bank_name()}
                                value={qr.bank_name}
                            />
                            <InfoRow
                                label={m.payment_qr_account_holder()}
                                value={qr.bank_holder}
                            />
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-muted-foreground shrink-0">
                                    {m.payment_qr_account_number()}
                                </span>
                                <div className="flex items-center gap-1">
                                    <span className="font-mono font-medium">
                                        {qr.bank_account}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            copyText(qr.bank_account)
                                        }
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        <Copy className="size-3.5" />
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center justify-between gap-2">
                                <span className="text-muted-foreground shrink-0">
                                    {m.payment_qr_content()}
                                </span>
                                <div className="flex items-center gap-1">
                                    <span className="font-mono font-medium">
                                        {qr.content}
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => copyText(qr.content)}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        <Copy className="size-3.5" />
                                    </button>
                                </div>
                            </div>
                            <div className="flex items-center justify-between border-t pt-2 font-semibold">
                                <span>{m.payment_qr_amount()}</span>
                                <span className="text-primary">
                                    {formatVND(qr.amount)}
                                </span>
                            </div>
                        </div>

                        {/* Status */}
                        <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                            <Loader2 className="size-4 animate-spin" />
                            <span>{m.payment_qr_waiting()}</span>
                        </div>
                    </div>
                )}

                <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onOpenChange(false)}
                >
                    {m.common_close()}
                </Button>
            </DialogContent>
        </Dialog>
    )
}

function InfoRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex items-center justify-between gap-2">
            <span className="text-muted-foreground shrink-0">{label}</span>
            <span className="font-medium">{value}</span>
        </div>
    )
}
